// Google Analytics 4 items of the store front: one shape for every event
// (lists, the product page, the cart, the checkout, the purchase), as the
// ecommerce reports and Google Ads read them —
//   item_id         the product's pid
//   item_name       its name
//   item_brand      the seller (the merchant that ships it)
//   item_category   the kind of product (its content type: aldoo-book, music-album, …)
//   item_category2  its first catalog category
//   item_variant    the options chosen (size, colour…)
//   price           the unit price paid, after its discount, in the cart's currency
//   discount        what the discount takes off a unit
//   index / item_list_id / item_list_name   where it was shown (a list event)
// Every amount is in the cart's currency, so the events of one visit add up.
import PubSub from "pubsub-js"
import { setStoreTracking } from "aldoo-ra/Store"
import { cartCurrency, convertToCart } from "./store/currency"
import { itemUnitPrice, lineProduct, lineUnitPrice, productCurrency } from "./checkout/cartItems"
import { normalizeProduct, parseRealPrice, profileOf } from "./utils/productUtils"

const units = (cents) => Math.round(cents) / 100
export const gaCurrency = () => (cartCurrency() || "eur").toUpperCase()

const variantOf = (line) => {
  const labels = line?.choiceLabels
  if (!labels || typeof labels !== "object") return undefined
  const text = Object.values(labels)
    .map((entry) => (entry?.value !== undefined ? entry.value : entry))
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(" / ")
  return text || undefined
}

/**
 * A product (or a cart line, `line`) as a GA4 item. `list`: { id, name } of
 * the list it is shown in, `index` its place there.
 */
export function ga4Item(productOrLine, { quantity, line, list, index } = {}) {
  const isLine = !!(productOrLine?.product || productOrLine?.lineKey || productOrLine?.options)
  const theLine = line || (isLine ? productOrLine : null)
  const product = normalizeProduct(theLine ? lineProduct(theLine) : productOrLine) || {}
  const from = productCurrency(product)
  const paid = theLine ? lineUnitPrice(theLine) : itemUnitPrice(product)
  const listPrice = parseRealPrice(product.real_price)?.amount ?? (parseInt(product.real_money_cost) || 0)
  const price = convertToCart(paid, from)
  const discount = listPrice > paid ? convertToCart(listPrice - paid, from) : 0
  const seller = theLine?.options?.seller?.name || product.publisher || undefined
  const categories = Array.isArray(product.categories) ? product.categories : []
  const item = {
    item_id: product.pid || theLine?.pid,
    item_name: product.name || theLine?.name || product.pid,
    ...(seller ? { item_brand: seller } : {}),
    // what kind of product it is: its profile
    ...(profileOf(product)?.id ? { item_category: String(profileOf(product).name || profileOf(product).id) } : {}),
    ...(categories[0] ? { item_category2: String(categories[0]) } : {}),
    ...(variantOf(theLine) ? { item_variant: variantOf(theLine) } : {}),
    price: units(price),
    ...(discount > 0 ? { discount: units(discount) } : {}),
    quantity: quantity ?? theLine?.quantity ?? 1,
  }
  if (list?.id) item.item_list_id = list.id
  if (list?.name) item.item_list_name = list.name
  if (Number.isInteger(index)) item.index = index
  return item
}

/** What items come to (the event's value), in the cart's currency. */
export const valueOf = (items) => Math.round(items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0) * 100) / 100

/** A GA4 event through aldoo-ra Tracking/google (topic, in the cart's currency). */
export function track(topic, items, extra = {}) {
  PubSub.publish(topic, { currency: gaCurrency(), value: valueOf(items), items, ...extra })
}

/** The framework store reads its lines the same way (cart, checkout, purchase events). */
export function registerStoreTracking() {
  setStoreTracking({
    item: (line) => ga4Item(line),
    currency: () => gaCurrency(),
  })
}
